import { validInt } from 'booksprout'
import { BaseModel } from '../modules/_base/base.model'

/**
 * The decisions behind a paged, sortable, optionally infinite-scrolling list.
 *
 * What lives here is everything that is arithmetic or shape: what args a read should carry, what the
 * next click on a sort header means, what an api result actually contains, which page the url is asking
 * for, and whether a lazy-load should go ahead.
 *
 * What does *not* live here is the state (`list`, `currentPage`, `skip`, …), the `$refs` to the
 * paginator and the infinite scroller, and the orchestration. `getData` is overridden by 17 pages,
 * `mergeGetArgs` by 7, `loadComplete` and `preparseList` by 4 each — so `load` and `lazyLoad` stay on
 * the mixin, where `this.*` still reaches the override.
 *
 * ── DO NOT lift `load`/`lazyLoad` in here the way `useCrud` took `save`/`autoSave` ───────────────
 *
 * That looks like the obvious next step and it is the wrong shape. `useCrud`'s orchestration reads a
 * handful of collaborators and returns; a save has one piece of state, the model. `load` and `lazyLoad`
 * *drive* ten fields — `loaded`, `list`, `currentPage`, `skip`, `totalRows`, `doneLoadingInfiniteScroll`,
 * `currentlyLoadingMoreData`, `scrollPageLoaded`, `sortBy`, `sortDescending` — and dispatch to four
 * overridable methods. A context threading all of that is strictly harder to read than the mixin, and
 * every caller would have to build it.
 *
 * The state is the component's, so the orchestration belongs with the state: a converted list page
 * declares those fields as refs and writes its own `load`, using the helpers below for the arithmetic.
 * That is what AuthorBase's list pages do, and what the phase-1 plan means by "move list state into the
 * module façade and the orchestration follows it there".
 */

export interface ListPageState {
  skip: number
  rowsPerPage: number
  sortBy: string | undefined
  sortDescending: boolean | undefined
  genericSearchTerm: string
}

export interface SortState {
  sortBy: string | undefined
  sortDescending: boolean | undefined
  previousSortDescending: boolean | undefined
}

export interface ShapedResult<TModel> {
  items: TModel[]
  /** Only paged endpoints report this; `void 0` means "the result was a plain array". */
  totalRows?: number
}

export function useList<TModel extends BaseModel> () {
  /**
   * `page * rowsPerPage - rowsPerPage` in one place — it was written out at four call sites.
   */
  const skipFor = (page: number, rowsPerPage: number) => page * rowsPerPage - rowsPerPage

  const makeGetArgs = (state: ListPageState, mergeGetArgs: any = {}, paramsToMerge: any = {}) => {
    return {
      skip: state.skip,
      take: state.rowsPerPage,
      sortBy: state.sortBy,
      descending: state.sortDescending,
      genericSearchTerm: state.genericSearchTerm,
      ...mergeGetArgs,
      ...paramsToMerge
    }
  }

  const sortIcon = (sortDescending: boolean | undefined) => {
    return 'arrow_' + (sortDescending ? 'downward' : sortDescending === void 0 ? '' : 'upward')
  }

  /**
   * Only after a load: an empty list mid-load is not "no results", it is "not yet".
   */
  const showNoResults = (listLength: number | undefined, loaded: boolean) => {
    return listLength === 0 && loaded
  }

  /**
   * The rows a paged list's page shows, as its "Showing … of …" header prints them: `13-24`. The last page
   * stops at the total rather than a whole page on, and a list with nothing in it shows `0`. [sc-28754]
   */
  const pageRange = (skip: number, rowsPerPage: number, totalRows: number) => {
    if (totalRows === 0) {
      return '0'
    }

    return `${skip + 1}-${Math.min(skip + rowsPerPage, totalRows)}`
  }

  /**
   * Clicking a sort header cycles descending -> ascending -> unsorted, and clicking a *different*
   * header starts that column over. Returns the next sort state rather than mutating anything.
   */
  const nextSort = (current: SortState, columnName: string): SortState => {
    let clearSort = false
    let sortDescending = current.sortDescending
    let previousSortDescending = current.previousSortDescending

    if (current.sortBy === columnName) {
      if (current.previousSortDescending === false) {
        sortDescending = void 0 // third click: back to unsorted
        clearSort = true
      } else {
        sortDescending = !sortDescending
      }
      previousSortDescending = sortDescending
    } else if (current.sortBy === void 0) {
      // sortBy was reset, so the next click starts descending again
      sortDescending = true
    } else {
      sortDescending = void 0
    }

    return {
      sortBy: clearSort ? void 0 : columnName,
      sortDescending,
      previousSortDescending
    }
  }

  /**
   * An endpoint returns either a paged result or a plain array; `totalRows` is what tells them apart.
   *
   * With infinite scroll the new items are appended to what is already on screen, minus anything
   * already there — a page boundary can repeat a row when the underlying data shifts between requests.
   *
   * The optional chaining on `currentList` is deliberate and carried over verbatim: a list that is
   * `undefined` stays `undefined` rather than becoming `[]`, which matters because `showNoResults`
   * tests `length === 0` and would otherwise start showing the empty panel where it used to show
   * nothing. baseListModule.spec pins it.
   */
  const shapeResult = (result: any, currentList: TModel[] | undefined, infiniteScroll: boolean): ShapedResult<TModel> => {
    const paged = result?.totalRows !== void 0
    const incoming: TModel[] = paged ? result.items : result

    if (!infiniteScroll) {
      return { items: incoming, totalRows: paged ? result.totalRows : void 0 }
    }

    const currentIds = currentList?.map(m => m.id)
    const fresh = incoming?.length ? incoming.filter(f => !currentIds?.includes(f.id)) : []

    return {
      items: currentList?.concat(fresh) as TModel[],
      totalRows: paged ? result.totalRows : void 0
    }
  }

  /**
   * Which page the url is asking for, or `void 0` when it is not asking for one. `?page=` is how a
   * visitor gets back to where they were after following a link out of a long list.
   */
  const pageFromQuery = (pageQuery: unknown, rowsPerPage: number): { currentPage: number, skip: number } | undefined => {
    const page = pageQuery + ''
    if (!validInt(page)) {
      return void 0
    }

    const currentPage = parseInt(page)
    return { currentPage, skip: skipFor(currentPage, rowsPerPage) }
  }

  const PAGE_PARAM = '?page='

  const urlWithoutPage = (href: string) => {
    const index = href.indexOf(PAGE_PARAM)
    return index === -1 ? href : href.substr(0, index)
  }

  const hasPageInUrl = (href: string) => href.indexOf(PAGE_PARAM) > -1

  /**
   * `href` naming `page` as the one an infinite list has reached.
   *
   * It used to lag a page behind, so that a row clicked on page 2 while page 3 loaded was still there on
   * Back -- the list came back holding the url's page alone. It only lagged on the first write, though, so
   * a row on page 3 clicked once page 4 had loaded was gone. The list now comes back with every page down
   * to the url's, which makes the lag pointless (see useListState's `loadScrollPage`). [sc-29024]
   */
  const urlForPage = (href: string, page: number) => `${urlWithoutPage(href)}${PAGE_PARAM}${page}`

  /**
   * Whether a lazy load should go ahead: the url page has been applied, infinite scroll is on, there
   * are at least a page's worth of rows to be scrolling through, and the last attempt was long enough
   * ago.
   *
   * NOTE, carried over as-is: the elapsed test is
   * `new Date(now - then).getMilliseconds()`, which returns only the milliseconds *component* of the
   * difference — so a gap of exactly 1s or 2s reads as 0 and blocks the load rather than allowing it.
   * Fixing it changes how eagerly every infinite list pages, so it is left alone and pinned by a test.
   */
  const shouldLazyLoad = (opts: {
    scrollPageLoaded: boolean
    infiniteScroll: boolean
    listLength: number | undefined
    rowsPerPage: number
    lastAttempt: Date
    minTimeBetweenCalls: number
    now?: Date
  }) => {
    const now = opts.now || new Date()
    const elapsed = new Date(now.getTime() - opts.lastAttempt.getTime()).getMilliseconds()

    return opts.scrollPageLoaded &&
      opts.infiniteScroll &&
      (opts.listLength || 0) >= opts.rowsPerPage &&
      elapsed > opts.minTimeBetweenCalls
  }

  /**
   * Infinite scroll is only switched on after page 1 has loaded, so the first lazy load jumps to 2.
   */
  const nextLazyPage = (currentPage: number) => currentPage === 1 ? 2 : currentPage + 1

  return {
    skipFor,
    makeGetArgs,
    sortIcon,
    showNoResults,
    pageRange,
    nextSort,
    shapeResult,
    pageFromQuery,
    urlWithoutPage,
    hasPageInUrl,
    urlForPage,
    shouldLazyLoad,
    nextLazyPage
  }
}
